import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ProductTable from './components/ProductTable';
import FilterControls from './components/FilterControls';
import BulkActionToolbar from './components/BulkActionToolbar';
import AddProductModal from './components/AddProductModal';
import BarcodeScanModal from './components/BarcodeScanModal';
import api from '../../utils/api';
import { exportToCSV } from '../../utils/exportUtils';
import { toast } from 'sonner';

const ProductCatalog = () => {
  const navigate = useNavigate();
  
  const [products, setProducts] = useState([]);

  const [selectedProducts, setSelectedProducts] = useState([]);
  const [filters, setFilters] = useState({
    search: '',
    category: '',
    stockStatus: '',
    supplier: '',
    minPrice: '',
    maxPrice: '',
    minStock: '',
    maxStock: ''
  });
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
  const [showAddModal, setShowAddModal] = useState(false);
  const [showBarcodeModal, setShowBarcodeModal] = useState(false);
  const [productInitialValues, setProductInitialValues] = useState(null);


  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/products');
        const list = Array.isArray(data) ? data : [];
        const mapped = list.map(p => ({
          id: p.id,
          name: p.name,
          description: p.description,
          sku: p.sku,
          category: p.category,
          supplier: p.supplier,
          price: p.price,
          cost: p.cost,
          stock: p.quantity ?? p.stock ?? 0,
          reorderPoint: p.reorderPoint ?? 0,
          barcode: p.barcode,
          weight: p.weight,
          dimensions: p.dimensions,
          isActive: p.isActive ?? true,
          trackInventory: p.trackInventory ?? true,
          lastUpdated: p.updatedAt || p.createdAt || new Date().toISOString(),
        }));
        setProducts(mapped);
      } catch (e) {
        console.error('Failed to load products', e);
        setProducts([]);
      }
    })();
  }, []);

  const categoryOptions = useMemo(() => {
    const set = new Set();
    products.forEach(p => {
      const cats = Array.isArray(p?.category) ? p.category : (p?.category ? [p.category] : []);
      cats.forEach(c => {
        if (c) set.add(c);
      });
    });
    const options = Array.from(set)
      .sort((a, b) => a.localeCompare(b))
      .map(c => ({ value: c, label: c }));
    return [{ value: '', label: 'All Categories' }, ...options];
  }, [products]);
  
  const supplierOptions = useMemo(() => {
    const set = new Set();
    products.forEach(p => {
      if (p?.supplier) set.add(p.supplier);
    });
    const options = Array.from(set)
      .sort((a, b) => a.localeCompare(b))
      .map(s => ({ value: s, label: s }));
    return [{ value: '', label: 'All Suppliers' }, ...options];
  }, [products]);

  const filteredAndSortedProducts = useMemo(() => {
    let filtered = products?.filter(product => {
      if (filters?.search) {
        const searchTerm = filters?.search?.toLowerCase();
        const matchesSearch = 
          product?.name?.toLowerCase()?.includes(searchTerm) ||
          product?.sku?.toLowerCase()?.includes(searchTerm) ||
          product?.description?.toLowerCase()?.includes(searchTerm);
        if (!matchesSearch) return false;
      }
      if (filters?.category) {
        const cats = Array.isArray(product?.category) ? product?.category : (product?.category ? [product?.category] : []);
        if (!cats.includes(filters?.category)) return false;
      }
      if (filters?.stockStatus) {
        if (filters?.stockStatus === 'out-of-stock' && product?.stock > 0) return false;
        if (filters?.stockStatus === 'low-stock' && (product?.stock === 0 || product?.stock > product?.reorderPoint)) return false;
        if (filters?.stockStatus === 'in-stock' && product?.stock <= product?.reorderPoint) return false;
      }
      if (filters?.supplier && product?.supplier !== filters?.supplier) return false;
      if (filters?.minPrice && product?.price < parseFloat(filters?.minPrice)) return false;
      if (filters?.maxPrice && product?.price > parseFloat(filters?.maxPrice)) return false;
      if (filters?.minStock && product?.stock < parseInt(filters?.minStock)) return false;
      if (filters?.maxStock && product?.stock > parseInt(filters?.maxStock)) return false;
      return true;
    });

    if (sortConfig?.key) {
      filtered?.sort((a, b) => {
        let aValue = a?.[sortConfig?.key];
        let bValue = b?.[sortConfig?.key];
        if (sortConfig?.key === 'price' || sortConfig?.key === 'stock') {
          aValue = parseFloat(aValue) || 0;
          bValue = parseFloat(bValue) || 0;
        } else if (sortConfig?.key === 'lastUpdated') {
          aValue = new Date(aValue);
          bValue = new Date(bValue);
        } else if (typeof aValue === 'string') {
          aValue = aValue?.toLowerCase();
          bValue = bValue?.toLowerCase();
        }
        if (aValue < bValue) return sortConfig?.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig?.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  }, [products, filters, sortConfig]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev?.key === key && prev?.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleSelectProduct = (productId, isSelected) => {
    setSelectedProducts(prev => 
      isSelected 
        ? [...prev, productId]
        : prev?.filter(id => id !== productId)
    );
  };

  const handleSelectAll = (isSelected) => {
    setSelectedProducts(isSelected ? filteredAndSortedProducts?.map(p => p?.id) : []);
  };

  const handleClearSelection = () => {
    setSelectedProducts([]);
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setSelectedProducts([]);
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      category: '',
      stockStatus: '',
      supplier: '',
      minPrice: '',
      maxPrice: '',
      minStock: '',
      maxStock: ''
    });
  };





  const handleAddProduct = async (productData) => {
    try {
      console.log('Product data received:', productData);
      
      // Force quantity to be a number using Number() instead of parseInt
      const stockQuantity = Number(productData.stock || 0);
      
      console.log('Stock quantity for API:', stockQuantity, typeof stockQuantity);
      
      const payload = {
        name: productData.name,
        description: productData.description,
        sku: productData.sku,
        category: productData.category,
        supplier: productData.supplier,
        price: productData.price,
        cost: productData.cost,
        quantity: stockQuantity, // Use the explicitly parsed value
        reorderPoint: productData.reorderPoint,
        barcode: productData.barcode,
        weight: productData.weight,
        dimensions: productData.dimensions,
        isActive: productData.isActive,
        trackInventory: productData.trackInventory,
        imageUrl: productData.imageUrl,
      };
      
      console.log('Payload being sent to API:', payload);
      
      let data;
      // Check if this is an update (has an ID) or a new product
      if (productData.id && products.some(p => p.id === productData.id)) {
        // Update existing product
        const { data: responseData } = await api.put(`/products/${productData.id}`, payload);
        data = responseData;
        
        // Update the product in the local state
        setProducts(prev => prev.map(p => 
          p.id === productData.id ? {
            ...p,
            name: data.name,
            description: data.description,
            sku: data.sku,
            category: data.category,
            supplier: data.supplier,
            price: data.price,
            cost: data.cost,
            stock: data.quantity ?? 0,
            reorderPoint: data.reorderPoint ?? 0,
            barcode: data.barcode,
            weight: data.weight,
            dimensions: data.dimensions,
            isActive: data.isActive ?? true,
            trackInventory: data.trackInventory ?? true,
            imageUrl: data.imageUrl,
            lastUpdated: data.updatedAt || new Date().toISOString(),
          } : p
        ));
      } else {
        // Add new product
        // CRITICAL FIX: Ensure quantity is properly set for new products
        let stockQuantity = 0;
        if (productData.quantity !== undefined && productData.quantity !== null) {
          stockQuantity = Number(productData.quantity);
        } else if (productData.stock !== undefined && productData.stock !== null) {
          stockQuantity = Number(productData.stock);
        }
        
        // Ensure it's a valid number
        if (isNaN(stockQuantity)) stockQuantity = 0;
        
        // Set both quantity and stock to ensure consistency
        payload.quantity = stockQuantity;
        payload.stock = stockQuantity;
        
        console.log('Final payload with forced quantity:', payload, 'Quantity value:', stockQuantity);
        
        const { data: responseData } = await api.post('/products', payload);
        data = responseData;
        
        console.log('Response from API:', data);
        
        // Add the new product to the local state
        // Log the response data to debug quantity issues
        console.log('API response data:', data);
        
        // CRITICAL FIX: Ensure quantity is properly mapped from the API response
        let finalStock = 0;
        if (data.quantity !== undefined && data.quantity !== null) {
          finalStock = Number(data.quantity);
        } else if (productData.quantity !== undefined && productData.quantity !== null) {
          finalStock = Number(productData.quantity);
        } else if (productData.stock !== undefined && productData.stock !== null) {
          finalStock = Number(productData.stock);
        }
        
        // Ensure it's a valid number
        if (isNaN(finalStock)) finalStock = 0;
        
        console.log('Final stock value for UI:', finalStock);
        
        const mapped = {
          id: data.id,
          name: data.name,
          description: data.description,
          sku: data.sku,
          category: data.category,
          supplier: data.supplier,
          price: data.price,
          cost: data.cost,
          stock: finalStock, // Use our carefully processed stock value
          reorderPoint: data.reorderPoint ?? 0,
          barcode: data.barcode,
          weight: data.weight,
          dimensions: data.dimensions,
          isActive: data.isActive ?? true,
          trackInventory: data.trackInventory ?? true,
          imageUrl: data.imageUrl,
          lastUpdated: data.updatedAt || data.createdAt || new Date().toISOString(),
        };
        setProducts(prev => [...prev, mapped]);
        toast.success('Product added successfully');
      }
      
      setShowAddModal(false);
    } catch (e) {
      console.error('Error adding/updating product:', e);
      throw e;
    }
  };

  const handleBulkAction = (action, value) => {
    console.log('Bulk action:', action, 'Value:', value, 'Products:', selectedProducts);
    // Local UI updates only for now
    switch (action) {
      case 'update-category':
        setProducts(prev => prev?.map(product => 
          selectedProducts?.includes(product?.id) 
            ? { ...product, category: value, lastUpdated: new Date()?.toISOString() }
            : product
        ));
        break;
      case 'adjust-price':
        const [type, amount] = value?.split('|');
        setProducts(prev => prev?.map(product => {
          if (!selectedProducts?.includes(product?.id)) return product;
          let newPrice = product?.price;
          const adjustment = parseFloat(amount) || 0;
          switch (type) {
            case 'increase':
              newPrice = product?.price + adjustment;
              break;
            case 'decrease':
              newPrice = Math.max(0, product?.price - adjustment);
              break;
            case 'set':
              newPrice = adjustment;
              break;
          }
          return { ...product, price: newPrice, lastUpdated: new Date()?.toISOString() };
        }));
        break;
      case 'update-supplier':
        setProducts(prev => prev?.map(product => 
          selectedProducts?.includes(product?.id) 
            ? { ...product, supplier: value, lastUpdated: new Date()?.toISOString() }
            : product
        ));
        break;
      case 'mark-discontinued':
        setProducts(prev => prev?.map(product => 
          selectedProducts?.includes(product?.id) 
            ? { ...product, isActive: false, lastUpdated: new Date()?.toISOString() }
            : product
        ));
        break;
      case 'delete':
        setProducts(prev => prev?.filter(product => !selectedProducts?.includes(product?.id)));
        break;
    }
    setSelectedProducts([]);
  };

  const handleExport = async () => {
    await exportToCSV('/export/products.csv', 'products-export');
  };

  const handleViewDetails = (productId) => {
    navigate(`/product-details/${productId}`);
  };

  const handleEditProduct = (productId) => {
    const productToEdit = products.find(p => p.id === productId);
    if (productToEdit) {
      setProductInitialValues(productToEdit);
      setShowAddModal(true);
    }
  };

  const handleDuplicateProduct = (productId) => {
    const originalProduct = products?.find(p => p?.id === productId);
    if (originalProduct) {
      const duplicatedProduct = {
        ...originalProduct,
        id: `${originalProduct?.id}-copy-${Date.now()}`,
        name: `${originalProduct?.name} (Copy)`,
        sku: `${originalProduct?.sku}-COPY`,
        lastUpdated: new Date()?.toISOString()
      };
      setProducts(prev => [...prev, duplicatedProduct]);
    }
  };

  const handleBarcodeScanned = async (barcode) => {
    try {
      // First try to find if product with this barcode already exists
      const { data } = await api.get(`/barcode/${barcode}`);
      if (data?.id) {
        handleViewDetails(data.id);
    } else {
        // If product doesn't exist, open add product modal with only barcode pre-filled
        setShowAddModal(true);
        // Only pass the barcode to the AddProductModal component
        setProductInitialValues({ barcode: barcode });
      }
    } catch (e) {
      console.warn('No product found for barcode or lookup failed');
      // Open add product modal with only barcode pre-filled
      setShowAddModal(true);
      // Only pass the barcode to the AddProductModal component
      setProductInitialValues({ barcode: barcode });
    }
  };

  return (
    <>
      <div className="w-full px-3 sm:px-4 lg:px-6 py-4 sm:py-6 lg:py-8">
        <Breadcrumb />
            
            {/* Page Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4 sm:mb-6 lg:mb-8">
              <div>
                <h1 className="text-xl sm:text-2xl md:text-3xl font-semibold text-foreground">Product Catalog</h1>
                <p className="text-xs sm:text-sm text-muted-foreground mt-1 sm:mt-2">
                  Manage your product inventory and track stock levels
                </p>
              </div>
              
              <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              <Button
                variant="outline"
                onClick={() => setShowBarcodeModal(true)}
                iconName="Scan"
                iconPosition="left"
                iconSize={18}
                className="flex-1 sm:flex-none justify-center"
              >
                <span className="hidden sm:inline">Scan Barcode</span>
                <span className="sm:hidden">Scan</span>
              </Button>
              
              <Button
                variant="outline"
                onClick={handleExport}
                iconName="Download"
                iconPosition="left"
                iconSize={18}
                className="flex-1 sm:flex-none justify-center"
              >
                <span className="hidden sm:inline">Export Report</span>
                <span className="sm:hidden">Export</span>
              </Button>
              
              <Button
                variant="default"
                onClick={() => {
                  setProductInitialValues(null);
                  setShowAddModal(true);
                }}
                iconName="Plus"
                iconPosition="left"
                iconSize={18}
                className="flex-1 sm:flex-none justify-center"
              >
                <span className="hidden sm:inline">Add Product</span>
                <span className="sm:hidden">Add</span>
              </Button>
            </div>
          </div>

          {/* Filter Controls */}
          <FilterControls
            filters={filters}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters}
            productCount={filteredAndSortedProducts?.length}
            categoryOptions={categoryOptions}
            supplierOptions={supplierOptions}
          />

          {/* Bulk Action Toolbar */}
          <BulkActionToolbar
            selectedCount={selectedProducts?.length}
            onBulkAction={handleBulkAction}
            onClearSelection={handleClearSelection}
            onExport={handleExport}
          />

          {/* Product Table */}
          <ProductTable
            products={filteredAndSortedProducts}
            selectedProducts={selectedProducts}
            onSelectProduct={handleSelectProduct}
            onSelectAll={handleSelectAll}
            onSort={handleSort}
            sortConfig={sortConfig}
            onViewDetails={handleViewDetails}
            onEditProduct={handleEditProduct}
          />

          {/* Quick Stats */}
          <div className="mt-4 sm:mt-6 lg:mt-8 grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3 lg:gap-4">
            <div className="bg-card border border-border rounded-lg p-2.5 sm:p-3 lg:p-4">
              <div className="flex items-center space-x-2">
                <Icon name="Package" size={16} className="text-primary sm:hidden" />
                <Icon name="Package" size={18} className="text-primary hidden sm:block lg:hidden" />
                <Icon name="Package" size={20} className="text-primary hidden lg:block" />
                <span className="text-xs sm:text-sm text-muted-foreground">Total Products</span>
              </div>
              <p className="text-lg sm:text-xl lg:text-2xl font-semibold text-foreground mt-1">{products?.length}</p>
            </div>
            
            <div className="bg-card border border-border rounded-lg p-2.5 sm:p-3 lg:p-4">
              <div className="flex items-center space-x-2">
                <Icon name="AlertTriangle" size={16} className="text-warning sm:hidden" />
                <Icon name="AlertTriangle" size={18} className="text-warning hidden sm:block lg:hidden" />
                <Icon name="AlertTriangle" size={20} className="text-warning hidden lg:block" />
                <span className="text-xs sm:text-sm text-muted-foreground">Low Stock</span>
              </div>
              <p className="text-lg sm:text-xl lg:text-2xl font-semibold text-foreground mt-1">
                {products?.filter(p => p?.stock > 0 && p?.stock <= p?.reorderPoint)?.length}
              </p>
            </div>
            
            <div className="bg-card border border-border rounded-lg p-2.5 sm:p-3 lg:p-4">
              <div className="flex items-center space-x-2">
                <Icon name="XCircle" size={16} className="text-error sm:hidden" />
                <Icon name="XCircle" size={18} className="text-error hidden sm:block lg:hidden" />
                <Icon name="XCircle" size={20} className="text-error hidden lg:block" />
                <span className="text-xs sm:text-sm text-muted-foreground">Out of Stock</span>
              </div>
              <p className="text-lg sm:text-xl lg:text-2xl font-semibold text-foreground mt-1">
                {products?.filter(p => p?.stock === 0)?.length}
              </p>
            </div>
            
            <div className="bg-card border border-border rounded-lg p-2.5 sm:p-3 lg:p-4">
              <div className="flex items-center space-x-2">
                <Icon name="DollarSign" size={16} className="text-success sm:hidden" />
                <Icon name="DollarSign" size={18} className="text-success hidden sm:block lg:hidden" />
                <Icon name="DollarSign" size={20} className="text-success hidden lg:block" />
                <span className="text-xs sm:text-sm text-muted-foreground">Total Value</span>
              </div>
              <p className="text-lg sm:text-xl lg:text-2xl font-semibold text-foreground mt-1">
                ${products?.reduce((sum, p) => sum + ((p?.price || 0) * (p?.stock || 0)), 0)?.toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      {/* Modals */}
      <AddProductModal
        isOpen={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          setProductInitialValues(null);
        }}
        onAddProduct={handleAddProduct}
        initialValues={productInitialValues}
      />
      <BarcodeScanModal
        isOpen={showBarcodeModal}
        onClose={() => setShowBarcodeModal(false)}
        onBarcodeScanned={(barcode, productData) => {
          console.log('Barcode scanned:', barcode, productData);
          if (productData) {
            // If product data was found, populate the form
            setProductInitialValues({
              ...productData,
              barcode: barcode
            });
            setShowAddModal(true);
          } else {
            // If no product data was found, open empty form with just the barcode
            setProductInitialValues({
              barcode: barcode
            });
            setShowAddModal(true);
          }
        }}
      />
    </>
  );
};

export default ProductCatalog;